import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  ReactNode,
} from "react";
import axios from "axios";
import styled from "styled-components";
import FallbackContainer from "../components/Fallbacks/Container";
import FallbackLoader from "../components/Fallbacks/Loader";
import Button from "../components/Buttons/Button";

import bg from "../assets/login-bg.jpg";
import logo from "../assets/bn-logo.png";
import { getWindowHeight } from "../utils";

const POLL_INTERVAL = 120;
const AUTH_STATE_KEY = "auth";

interface AuthState {
  accessToken?: string;
  active_features?: string[];
  isLoggedIn: boolean;
  profileUrl: string;
  user?: number;
  validUntil?: number;
}

export const AuthContext = createContext<AuthState | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [hydrated, setHydrated] = useState<boolean>(false);
  const [authState, setAuthState] = useState<AuthState>(() => {
    const storedState = sessionStorage.getItem(AUTH_STATE_KEY);
    return storedState ? JSON.parse(storedState) : { isLoggedIn: false };
  });
  const checkAuthStatus = async () => {
    try {
      const response = await axios.get<AuthState>("/o/user/");
      sessionStorage.setItem(AUTH_STATE_KEY, JSON.stringify(response.data));
      setAuthState(response.data);
    } catch (err) {
      console.error(err);
    } finally {
      setHydrated(true);
    }
  };
  useEffect(() => {
    checkAuthStatus();
    const interval = setInterval(checkAuthStatus, POLL_INTERVAL * 1000);
    const handleOnline = () => {
      checkAuthStatus();
    };
    window.addEventListener("online", handleOnline);
    return () => {
      clearInterval(interval);
      window.removeEventListener("online", handleOnline);
    };
  }, []);
  if (!hydrated) {
    return <FallbackLoader />;
  }
  return authState.isLoggedIn ? (
    <AuthContext.Provider value={authState}>{children}</AuthContext.Provider>
  ) : (
    <Container>
      <div className="login-container">
        <Logo src={logo} alt="Boerennatuur" />
        <h2>Inloggen</h2>
        <Button
          color="green"
          onClick={() => (window.location.href = "/o/login/")}
          label="Log in met SCAN-GIS"
        />
      </div>
    </Container>
  );
};

export function fetchUserID(id: number) {
  return window.btoa(`UserNode:${id}`);
}

const Container = styled(FallbackContainer)`
  display: flex;
  justify-content: center;
  align-items: center;
  height: ${getWindowHeight()};
  background-color: #f5f5f5;
  font-family: "Roboto";
  background-position: center center;
  background-attachment: fixed;
  background-repeat: no-repeat;
  background-size: cover;
  background-image: url(${bg});
  h2 {
    color: var(--donkergrijs, #212529);
    text-align: center;
    /* H2 */
    font-size: 30px;
    font-family: Roboto;
    font-weight: 700;
    line-height: 120%;
  }
  .login-container {
    display: flex;
    flex-direction: column;
    padding-top: 44px;
    padding-bottom: 44px;
    background-color: #fafafa;
    padding-left: 120px;
    padding-right: 120px;
    border-radius: 10px;
    text-align: center;
    min-height: ${getWindowHeight(40)};
  }
`;

const Logo = styled.img`
  padding-bottom: 44px;
`;

export const useAuth = (): AuthState | undefined => useContext(AuthContext);
export default AuthProvider;
